import { ManagerBase, MouseEventResult } from './manager-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { FormArray, FormGroup } from '@angular/forms';
import { ParkingRowElement } from '../elements/parking/parkingrow';
import { ParkingSpaceElement } from '../elements/parking/parkingspace';
import { Gate } from '../elements/access-control/gate';
import { Lane } from '../elements/access-control/lane';
import { GuidanceLight } from '../elements/devices/guidance-light';
import { PolygonElement } from '../elements/polygon-element';
import { LevelDesignerElementBase } from '../elements/element-base';

export class DeleteManager extends ManagerBase {
    constructor(levelDesigner: LevelDesignerComponent, canvas: HTMLCanvasElement) {
        super(levelDesigner, canvas);
    }

    public override handleKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Delete' || event.key === 'Backspace') {
            this.deleteSelectedElements();
        }
    }

    private deleteSelectedElements(): void {
        const selectedElements = [...this.levelDesigner.selectedElements];
        if (selectedElements.length === 0 || selectedElements.some(el => el.isLocked())) {
            return;
        }

        selectedElements.forEach(element => {
            if (element instanceof ParkingRowElement) {
                this.deleteRowAndChildren(element);
                return;
            }

            // Remove from elements array
            const index = this.levelDesigner.elements.indexOf(element);
            if (index > -1) {
                this.levelDesigner.elements.splice(index, 1);
            }

            // Remove from form arrays
            if (this.levelDesigner.levelData) {
                // First try to remove from the element's declared parent form group
                if (element.ParentFormGroupName) {
                    const parentArray = this.levelDesigner.levelData.get(element.ParentFormGroupName) as FormArray;
                    if (parentArray) {
                        const formIndex = parentArray.controls.indexOf(element.formGroup);
                        if (formIndex > -1) {
                            parentArray.removeAt(formIndex);
                            return; // Exit if successfully removed
                        }
                    }
                    
                }

                // If not found in parent form group, check specific cases
                if (element instanceof Lane) {
                    // Find parent gate and remove lane from its Lanes array
                    const gates = this.levelDesigner.levelData.get('Gates') as FormArray;
                    for (const gateControl of gates.controls) {
                        const lanes = gateControl.get('Lanes') as FormArray;
                        const laneIndex = lanes.controls.indexOf(element.formGroup);
                        if (laneIndex > -1) {
                            lanes.removeAt(laneIndex);
                            break;
                        }
                    }
                }
                else if (element instanceof GuidanceLight) {
                    // Find parent row and remove light
                    const rows = this.levelDesigner.levelData.get('Rows') as FormArray;
                    for (const rowControl of rows.controls) {
                        const lights = rowControl.get('GuidanceLightConfigurations') as FormArray;
                        if (lights) {
                            const lightIndex = lights.controls.indexOf(element.formGroup);
                            if (lightIndex > -1) {
                                lights.removeAt(lightIndex);
                                break;
                            }
                        }
                    }
                }
                else if (element instanceof ParkingSpaceElement) {
                    const deletedSpaceId: string | null = element.formGroup.get('Id')?.value ?? null;
                    const rows = this.levelDesigner.levelData.get('Rows') as FormArray;
                    for (const rowControl of rows.controls) {
                        const spaces = rowControl.get('Spaces') as FormArray;
                        const spaceIndex = spaces.controls.indexOf(element.formGroup);
                        if (spaceIndex > -1) {
                            spaces.removeAt(spaceIndex);
                            if (deletedSpaceId) {
                                this.clearPortAssignmentsForSpace(rowControl as FormGroup, deletedSpaceId);
                            }

                            break;
                        }
                    }
                }

                // Remove from parent element's visual children list
                if(element.currentParentElement != null && element.currentParentElement instanceof PolygonElement) {
                    const childIndex = element.currentParentElement.elements.indexOf(element);
                    if (childIndex > -1) {
                        element.currentParentElement.elements.splice(childIndex, 1);
                    }
                }
            }
        });

        // Clear selection and update
        this.levelDesigner.setSelectedElements([]);
        if (this.levelDesigner.levelData) {
            this.levelDesigner.levelData.markAsDirty();
        }
        this.levelDesigner.saveState();
        this.levelDesigner.redraw();
    }

    private deleteRowAndChildren(row: ParkingRowElement): void {
        const children = [...row.elements];

        for (const child of children) {
            const idx = this.levelDesigner.elements.indexOf(child);
            if (idx > -1) {
                this.levelDesigner.elements.splice(idx, 1);
            }

            if (child instanceof PolygonElement && child.elements.length > 0) {
                const grandchildren = [...child.elements];
                for (const grandchild of grandchildren) {
                    const gIdx = this.levelDesigner.elements.indexOf(grandchild);
                    if (gIdx > -1) {
                        this.levelDesigner.elements.splice(gIdx, 1);
                    }
                }
            }
        }

        // Remove the row itself from the flat elements list
        const rowIdx = this.levelDesigner.elements.indexOf(row);
        if (rowIdx > -1) {
            this.levelDesigner.elements.splice(rowIdx, 1);
        }

        // Remove the row's FormGroup from the Rows FormArray
        if (this.levelDesigner.levelData) {
            const rows = this.levelDesigner.levelData.get('Rows') as FormArray;
            if (rows) {
                const formIdx = rows.controls.indexOf(row.formGroup);
                if (formIdx > -1) {
                    rows.removeAt(formIdx);
                }
            }
        }

        // Remove from the level's visual children list
        if (row.currentParentElement instanceof PolygonElement) {
            const parentIdx = row.currentParentElement.elements.indexOf(row);
            if (parentIdx > -1) {
                row.currentParentElement.elements.splice(parentIdx, 1);
            }
        }
    }

   private clearPortAssignmentsForSpace(rowControl: FormGroup, deletedSpaceId: string): void {
        const lights = rowControl.get('GuidanceLightConfigurations') as FormArray;
        if (!lights) {
            return;
        }

        for (const lightControl of lights.controls) {
            const portAssignments = (lightControl as FormGroup).get('PortAssignments') as FormArray;
            if (!portAssignments) {
                continue;
            }

            for (const portControl of portAssignments.controls) {
                const portGroup = portControl as FormGroup;
                if (portGroup.get('ParkingSpaceId')?.value === deletedSpaceId) {
                    portGroup.get('ParkingSpaceId')?.setValue(null);
                }
            }

            const lightElement = this.levelDesigner.elements.find(
                e => e instanceof GuidanceLight && e.formGroup === lightControl
            ) as GuidanceLight | undefined;

            if (lightElement) {
                for (let port = 1; port <= 6; port++) {
                    const assigned = lightElement.getPortAssignment(port);
                    if (assigned && assigned.formGroup.get('Id')?.value === deletedSpaceId) {
                        lightElement.updatePortAssignment(port, null);
                    }
                }
            }
        }
    }

    public OnDraw(): void {
        // No drawing needed for delete manager
    }
} 
